/**
 * <ShareThreeOptions /> - Platform-wide 3-option share component
 *
 * Per the Shelsea Pilot Handoff (4) + Pass 2 spec:
 * Replaces the legacy "Invite & Copy link" CTA everywhere a host shares a link.
 *
 * Three side-by-side options:
 *   1. One-click copy square - copies URL to clipboard
 *   2. Up-arrow share icon   - universal share (OS native share sheet)
 *   3. Open in new tab       - preview as the recipient sees it
 *
 * Usage:
 *   <ShareThreeOptions url="https://..." shareTitle="Privé Beauté" testId="host-profile-share" />
 */
import { Copy, Share, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export default function ShareThreeOptions({
  url,
  shareTitle = "Phygital",
  shareText = "",
  className = "",
  size = "md",
  variant = "default",
  testId = "share-three-options",
  onCopy,
  onShare,
  onOpen,
}) {
  if (!url) return null;

  const iconPx = { sm: 16, md: 18, lg: 22 }[size] || 18;
  const btnClass = cn(
    "inline-flex items-center justify-center rounded-lg transition-all border",
    size === "sm" ? "w-8 h-8" : size === "lg" ? "w-11 h-11" : "w-10 h-10",
    variant === "ghost"
      ? "bg-transparent hover:bg-black/5 border-transparent"
      : "bg-white hover:bg-black hover:text-white border-black/10 hover:border-black"
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      onCopy?.(url);
    } catch {
      toast.error("Couldn't copy - try again");
    }
  };

  const handleShare = async () => {
    onShare?.(url);
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url });
      } catch {
        /* user cancelled - silent */
      }
    } else {
      // Fallback: copy + toast
      handleCopy();
    }
  };

  const handleOpen = () => {
    onOpen?.(url);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={cn("inline-flex items-center gap-2", className)}
      data-testid={testId}
      role="group"
      aria-label="Share options"
    >
      <button
        type="button"
        onClick={handleCopy}
        className={btnClass}
        title="Copy link"
        aria-label="Copy link"
        data-testid={`${testId}-copy`}
      >
        <Copy size={iconPx} />
      </button>
      <button
        type="button"
        onClick={handleShare}
        className={btnClass}
        title="Share"
        aria-label="Share"
        data-testid={`${testId}-share`}
      >
        <Share size={iconPx} />
      </button>
      <button
        type="button"
        onClick={handleOpen}
        className={btnClass}
        title="Open in new tab"
        aria-label="Open in new tab"
        data-testid={`${testId}-open`}
      >
        <ExternalLink size={iconPx} />
      </button>
    </div>
  );
}

export { ShareThreeOptions };
